import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import logo from "../../assets/logo.png";
import { LogOut } from "lucide-react";

type NavItem = {
  name: string;
  path: string;
  icon: React.ReactNode;
};

const Icon = ({ path }: { path: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    className="h-5 w-5"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d={path} />
  </svg>
);

const navGroups: { title: string; items: NavItem[]; restricted?: boolean }[] = [
  {
    title: "Main",
    items: [
      {
        name: "Dashboard",
        path: "/learn-with-us",
        icon: (
          <Icon path="M3 13h8V3H3v10Zm10 8h8V3h-8v18ZM3 21h8v-6H3v6Z" />
        ),
      },
      {
        name: "Lead & Enquiry",
        path: "/learn-with-us/lead-enquiry",
        icon: (
          <Icon path="M12 3c-4.97 0-9 4.03-9 9v7c0 1.1.9 2 2 2h4v-8H5v-1c0-3.87 3.13-7 7-7s7 3.13 7 7v1h-4v8h4c1.1 0 2-.9 2-2v-7c0-4.97-4.03-9-9-9z" />
        ),
      },
      {
        name: "Courses",
        path: "/learn-with-us/courses",
        icon: (
          <Icon path="M4 19.5A2.5 2.5 0 0 1 6.5 17H20M4 4.5A2.5 2.5 0 0 1 6.5 2H20v20H6.5A2.5 2.5 0 0 0 4 19.5v-15Z" />
        ),
      },
      {
        name: "Batches",
        path: "/learn-with-us/batches",
        icon: (
          <Icon path="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
        ),
      },
      {
        name: "Students",
        path: "/learn-with-us/students",
        icon: (
          <Icon path="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM22 21v-2a4 4 0 0 0-3-3.87" />
        ),
      },
    ],
  },
  {
    title: "Academic",
    items: [
      {
        name: "Teachers",
        path: "/learn-with-us/teachers",
        icon: (
          <Icon path="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5Z" />
        ),
      },
      {
        name: "Question Bank",
        path: "/learn-with-us/questions",
        icon: (
          <Icon path="M9.09 9a3 3 0 1 1 5.82 1c0 2-3 2-3 4M12 17h.01M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
        ),
      },
      {
        name: "Test Series",
        path: "/learn-with-us/tests",
        icon: (
          <Icon path="M9 11l3 3L22 4M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
        ),
      },
      {
        name: "Live Classes",
        path: "/learn-with-us/live-classes",
        icon: (
          <Icon path="M23 7l-7 5 7 5V7ZM14 5H3a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2Z" />
        ),
      },
    ],
  },
  {
    title: "Ordinates",
    restricted: true, // Super Admin targeted tag
    items: [
      {
        name: "Pending Approval",
        path: "/learn-with-us/pending-approvals",
        icon: (
          <Icon path="M12 11c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
        ),
      },
      {
        name: "Subordinates",
        path: "/learn-with-us/subordinates",
        icon: (
          <Icon path="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z" />
        ),
      },
    ],
  },
  {
    title: "Business",
    items: [
      {
        name: "Payments",
        path: "/learn-with-us/payments",
        icon: (
          <Icon path="M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7H14a3.5 3.5 0 0 1 0 7H6" />
        ),
      },
      {
        name: "Reports",
        path: "/learn-with-us/reports",
        icon: <Icon path="M3 3v18h18M7 15l4-4 3 3 5-7" />,
      },
      {
        name: "Settings",
        path: "/learn-with-us/settings",
        icon: (
          <Icon path="M12 15.5A3.5 3.5 0 1 0 12 8a3.5 3.5 0 0 0 0 7.5ZM19.4 15a1.7 1.7 0 0 0 .34 1.88l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06A1.7 1.7 0 0 0 15 19.4a1.7 1.7 0 0 0-1 .6 1.7 1.7 0 0 0-.4 1.1V21a2 2 0 1 1-4 0v-.1A1.7 1.7 0 0 0 8.6 19.4a1.7 1.7 0 0 0-1.88.34l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.7 1.7 0 0 0 4.6 15a1.7 1.7 0 0 0-.6-1 1.7 1.7 0 0 0-1.1-.4H3a2 2 0 1 1 0-4h.1A1.7 1.7 0 0 0 4.6 8.6a1.7 1.7 0 0 0-.34-1.88l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.7 1.7 0 0 0 9 4.6a1.7 1.7 0 0 0 1-.6 1.7 1.7 0 0 0 .4-1.1V3a2 2 0 1 1 4 0v.1A1.7 1.7 0 0 0 15.4 4.6a1.7 1.7 0 0 0 1.88-.34l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.7 1.7 0 0 0 19.4 9c.4.2.7.5.9.9.2.4.3.8.3 1.1s-.1.7-.3 1.1c-.2.4-.5.7-.9.9Z" />
        ),
      },
    ],
  },
];

const AdminLayout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();

  const adminAuthData = JSON.parse(localStorage.getItem("mediit_admin_auth") || "{}");
  const adminName = adminAuthData.name || "Admin";
  const adminRole = adminAuthData.role || "Super Admin";
  const initial = adminName.charAt(0).toUpperCase();

  const handleLogout = () => {
    localStorage.removeItem("mediit_admin_auth");
    sessionStorage.clear();
    navigate("/LMS/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#f5f7fb] text-slate-900 font-['Inter']">
      {sidebarOpen && (
        <button
          onClick={() => setSidebarOpen(false)}
          className="fixed inset-0 z-40 bg-slate-950/40 lg:hidden"
        />
      )}

      <aside
        className={`fixed left-0 top-0 z-50 h-full w-[260px] border-r border-slate-200 bg-white transition-transform duration-300 lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex h-20 items-center gap-3 border-b border-slate-100 px-5">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white shadow-lg shadow-blue-500/20 overflow-hidden">
            <img
              src={logo}
              alt="Mediit Academy Logo"
              className="h-full w-full object-contain p-1"
            />
          </div>

          <div>
            <h1 className="text-lg font-black leading-tight text-slate-950">
              MediitAdmin
            </h1>
            <p className="text-xs font-bold text-slate-400">LMS Management</p>
          </div>
        </div>

        <nav className="h-[calc(100vh-80px)] overflow-y-auto px-4 py-5">
          {navGroups.map((group) => {
            // Role checking for safety deployment
            if (group.restricted && adminRole !== "Super Admin") {
              return null;
            }

            return (
              <div key={group.title} className="mb-6">
                <p className="mb-2 px-3 text-[11px] font-black uppercase tracking-wider text-slate-400">
                  {group.title}
                </p>

                <div className="space-y-1">
                  {group.items.map((item) => (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      end={item.path === "/learn-with-us"}
                      onClick={() => setSidebarOpen(false)}
                      className={({ isActive }) =>
                        `flex items-center gap-3 rounded-2xl px-4 py-3 text-sm font-extrabold transition ${
                          isActive
                            ? "bg-blue-600 text-white shadow-lg shadow-blue-600/20"
                            : "text-slate-600 hover:bg-slate-100 hover:text-slate-950"
                        }`
                      }
                    >
                      <span className="shrink-0">{item.icon}</span>
                      <span>{item.name}</span>
                    </NavLink>
                  ))}
                </div>
              </div>
            );
          })}
        </nav>
      </aside>

      <div className="lg:pl-[260px]">
        <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/90 backdrop-blur-xl">
          <div className="flex h-20 items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setSidebarOpen(true)}
                className="flex h-11 w-11 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-700 lg:hidden"
              >
                <svg
                  viewBox="0 0 24 24"
                  className="h-6 w-6"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>

              <div>
                <h2 className="text-xl font-black text-slate-950">
                  Dashboard
                </h2>
                <p className="hidden text-sm font-semibold text-slate-500 sm:block">
                  Welcome back, manage your academy from here
                </p>
              </div>
            </div>

            <div className="flex flex-1 items-center justify-end gap-3">
              <div className="hidden h-11 w-full max-w-md items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 md:flex">
                <svg
                  viewBox="0 0 24 24"
                  className="h-5 w-5 text-slate-400"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M21 21l-4.35-4.35M10.5 18a7.5 7.5 0 1 1 0-15 7.5 7.5 0 0 1 0 15Z" />
                </svg>

                <input
                  placeholder="Search students, courses, tests..."
                  className="w-full bg-transparent text-sm font-semibold outline-none placeholder:text-slate-400"
                />
              </div>

              <button className="flex h-11 w-11 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 hover:bg-slate-50">
                <svg
                  viewBox="0 0 24 24"
                  className="h-5 w-5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 7h18s-3 0-3-7M13.73 21a2 2 0 0 1-3.46 0" />
                </svg>
              </button>

              <div 
                onClick={() => navigate("/learn-with-us/ad-profile")}
                className="flex h-12 cursor-pointer items-center gap-3 rounded-2xl border border-slate-200 bg-white px-3 shadow-sm transition hover:bg-slate-50"
                title="View Profile"
              >
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-slate-950 text-sm font-black text-white">
                  {initial}
                </div>

                <div className="hidden pr-1 text-left sm:block">
                  <p className="max-w-[120px] truncate text-sm font-black leading-none text-slate-950">
                    {adminName}
                  </p>
                  <p className="mt-1 text-xs font-bold text-slate-500">
                    {adminRole}
                  </p>
                </div>
              </div>

              <button
                onClick={handleLogout}
                className="flex h-12 items-center gap-2 rounded-2xl border border-red-100 bg-red-50 px-4 text-sm font-black text-red-600 shadow-sm transition hover:bg-red-100"
              >
                <LogOut size={18} />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>
        </header>

        <main className="p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;